import { Menu, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { submitSupportRequestFunction } from "../data/firebase";
import { useToast } from "@/hooks/use-toast";

const faqs = [
  {
    question: "I've lost my login details. How do I find them?",
    answer:
      "When you signed up, we sent your login details to your inbox. Please search for the email titled: 'Welcome to Options AI'. Inside you'll find details of your username and login link to access Options AI.\n\nIf you can't find it, check your spam folder or promotions tab. Sometimes our emails end up there. If you still can't find it, please check any other email accounts you may have. In case you signed up with a different email address.\n\nAlternatively, please login via our homepage: <a href='https://optionsapp.com/' class='text-blue-600 hover:underline' target='_blank'>www.optionsapp.com</a> and tap 'LOGIN' in the top right corner.",
  },
  {
    question: "I've forgotten my password. How do I change it?",
    answer:
      "You can change your password on the App via the 'My Account' page. Simply navigate to: My Account -> Change Password. Scroll down the page and you can update your password at any time.<br><br>Or alternatively, if you log out of the app, you will find a link to reset your password on the <a href='https://chat.optionsapp.com/login/' class='text-blue-600 hover:underline'>login page</a>. Look for where it says 'Forgot Password?' and follow the steps to change it.",
  },
  {
    question: "How can I access the App. I keep forgetting the url link?",
    answer:
      "The url to access Options AI is: <a href='https://chat.optionsapp.com/login/' class='text-blue-600 hover:underline'>https://chat.optionsapp.com/</a><br><br>To access the app more easily, follow these two steps:<br><br>1. After logging into the app, tap the menu button. Or, on Android tap the three dots.<br><br>2. Tap 'Add Bookmark' or 'Add to Favourites'<br><br>Or alternatively, if you want Options AI to appear as an App on your phone's home screen, tap 'Add to Home Screen':<br><br>If you don't see Add to Home Screen, scroll down to the bottom of the list, tap Edit Actions, then tap + Add to Home Screen.<br><br>For more help on this, please visit the <a href='https://optionsapp.com/quick-start-guide/' class='text-blue-600 hover:underline' target='_blank'>quick start user guide here.</a>",
  },
  {
    question: "What's included in Options AI Premium?",
    answer:
      "This includes detailed chat analysis, unlimited AI-generated replies, real-time personalised coaching, screenshot text extraction, and your own AI Wingman that's available 24/7 whenever you need advice.\n\nUnlike generic AI, Options AI remembers every girl you're talking to, your previous conversations, and what she's responded well to, so every response becomes increasingly tailored to your unique situation.\n\nPremium is powered by cutting-edge AI technology built on the complete OPTIONS Texting System, giving you fast, personalised guidance whenever you need it.",
  },
  {
    question: "What's included in the 1 Month Access Pass?",
    answer:
      "If you purchased a 1 Month Access Pass you get 30 days full access to Options AI. After the 30 days expire you'll have the choice to either purchase a new month pass or subscribe & save with Options AI Premium.",
  },
  {
    question: "How do I cancel my subscription?",
    answer:
      "The cancellation process depends on how you access Options AI.\n\nIf you're subscribed to Options AI as a standalone app, you can cancel your subscription from within the app by navigating to:\n\nMy Account -> Manage Subscription.\n\nAlternatively, you can email our Customer Happiness Team at <a href='mailto:support@optionsapp.com' class='text-blue-600 hover:underline'>support@optionsapp.com</a> and we'll be happy to help.\n\nIf your Options AI access is included with your Options Academy membership, you'll need to cancel your Academy membership through the OptionsTexting.com Members Portal. If you're unsure where to do this, simply email <a href='mailto:support@optionstexting.com' class='text-blue-600 hover:underline'>support@optionstexting.com</a> and we'll point you in the right direction.\n\nIf you purchased a 1 Month Access Pass you are not on a subscription, so you don't need to cancel. After 30 days your access will expire and you'll have the opportunity to buy a new pass.",
  },
  {
    question:
      "Can I upload dating app bios or profile pic screenshots of my matches?",
    answer:
      "Dating App profile images and bio's are not compatible. This app is exclusively for text screenshots. If you need help crafting a first message after matching with a girl on a Dating App, simply tell the AI what you see in her photos or bio – interests, hobbies, or anything unique about her you want to mention in the first text.\n\nFor the best examples of how to do this, check out the <a href='https://optionsapp.com/quick-start-guide/' class='text-blue-600 hover:underline' target='_blank'>quick start user guide here.</a>",
  },
  {
    question:
      "The screenshot upload function didn't display all the texts from my image?",
    answer:
      "Text screenshots from all your favourite dating apps and messenger apps work with Options AI (e.g. Tinder, Bumble, Hinge, iMessage, IG, WhatsApp, etc).\n\nDepending on what messenger or dating app you're using, some apps like WhatsApp and IG have a 'down' arrow to return to the most recent message. When you're taking screenshots this down arrow might occasionally get in the way of one of your texts.\n\nThe AI is usually able to predict the text hidden behind the arrow, but 1-2% of the time you'll need to manually edit the text after uploading the screenshot.\n\nOr, sometimes a text at the bottom of the screen doesn't fit when you do a screenshot.\n\nIf this happens, don't worry. You can either crop the image before uploading it to Options AI. Or you can manually add, edit, or delete texts on Options AI before you hit 'Analyze Chat'.\nOn the App, simply tap the penicil icon to edit a text, or tap the icon to add an extra text.\n\nFor more help on this, please visit the <a href='https://optionsapp.com/quick-start-guide/' class='text-blue-600 hover:underline' target='_blank'>quick start user guide here.</a>",
  },
  {
    question: "How does the Chat Analysis work?",
    answer:
      "Our AI analyzes your conversations to provide insights about communication patterns, engagement levels, and potential areas for improvement. This helps you better understand the dynamics of your conversations and what to text her next.\n\nYou can ask the AI anything to help you achieve your goals – it will tell you exactly what to say next depending on your situation.\n\nIf she's ghosting you, you want to flirt, make her laugh, or ask her on a date. Options AI has your back.\n\nUsing the chat prompt function: 'Ask me anything' - tell the AI your situation, how long she hasn't replied, or what date suggestion you have in mind, and let the AI work with you to achieve your goals.\n\nYou don't always have to upload text screenshots with your matches. You can simply use the chat prompt straight away.",
  },
  {
    question: "I can't download Options AI from the AppStore or Google Play?",
    answer:
      "Options AI is a Web App, it's not something you download from the App Store or Google Play. But a web app is effectively the exact same thing.\n\nWhen using Options AI, make sure to bookmark it to your web browser 'favourites', or tap 'Add to Home Screen' on your phone so you can access it like all your other favourite Apps.\n\nFor more quick tips to make the most out of Options AI, please visit the <a href='https://optionsapp.com/quick-start-guide/' class='text-blue-600 hover:underline' target='_blank'>quick start user guide here.</a>",
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  const formatAnswer = (text: string) => {
    return text.replace(/\n/g, "<br>");
  };

  return (
    <div className="border-gray-200">
      <button
        className="flex w-full items-center justify-between p-4 text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-base font-medium text-gray-900">{question}</span>
        <ChevronDown
          className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div
              className="p-4 text-sm text-gray-500"
              dangerouslySetInnerHTML={{ __html: formatAnswer(answer) }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Help() {
  const { setOpenMobile } = useSidebar();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitSupportRequestFunction(formData);
      toast({
        title: "Message sent",
        description: "We'll get back to you as soon as possible.",
        duration: 5000,
      });
      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: "Failed to send your message. Please try again later.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-12 sm:mt-0 flex-1 h-full overflow-y-auto">
      <div className="container max-w-2xl mx-auto pt-0 sm:pt-16 space-y-8 px-4 pb-60">
        {/* // <div className="mt-12 sm:mt-0 flex-1 h-full overflow-y-auto">
    //   <div className="container max-w-2xl mx-auto py-0 space-y-8 px-4 pb-60"> */}
        {/* <div>
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setOpenMobile(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div> */}

        <div className="flex items-center gap-4 mt-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Support</h1>
            <p className="text-sm text-gray-500">
              Please read our FAQs to find a quick solution to any problems you
              might be facing
            </p>
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">
              Frequently Asked Questions
            </h2>
            <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-200">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Additional Help</h2>
            <div className="space-y-4 text-sm text-gray-500">
              <p>Still can't find what you're looking for?</p>

              <p>
                For more help with using the app, please visit the{" "}
                <a
                  href="https://optionsapp.com/quick-start-guide/"
                  target="_blank"
                  className="text-blue-600 hover:underline"
                >
                  Options AI quick start guide here
                </a>
                .
              </p>

              <p>
                But if you still can't find the help you need, please email us
                directly at:{" "}
                <a
                  href="mailto:support@optionsapp.com"
                  className="text-blue-600 hover:underline"
                >
                  support@optionsapp.com
                </a>{" "}
                and one of our dedicated support team will get back to you.
              </p>

              <div className="pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Provide feedback, or report a Bug
                </h3>
                <p>
                  If you have any recommendations for us to improve the App, or
                  you want to report a bug you're experiencing, please get in
                  touch. We'd love to hear from you.
                </p>
                <p className="mt-2">
                  Email us at{" "}
                  <a
                    href="mailto:support@optionsapp.com"
                    className="text-blue-600 hover:underline"
                  >
                    support@optionsapp.com
                  </a>{" "}
                  or please use the form below.
                </p>
                <p className="mt-2">
                  Our dev team will fix any issues, and level up the app pronto
                  🙂
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* <h2 className="text-xl font-semibold">Contact Us</h2>
            <p className="text-sm text-gray-500">
              Can't find what you're looking for or want to give us feedback?{" "}
              <br />
              Email us at{" "}
              <a
                href="mailto:support@optionsapp.com"
                className="text-blue-600 hover:underline"
              >
                support@optionsapp.com
              </a>{" "}
              or use the form below and we'll get back to you as soon as
              possible.
            </p> */}

            <form onSubmit={handleSubmit} className="space-y-4 mb-8">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    Name
                  </label>
                  <Input
                    id="name"
                    value={formData.name}
                    className="bg-white"
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    className="bg-white"
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </div>
                {/* <div className="space-y-2">
                  <label htmlFor="subject" className="text-sm font-medium">
                    Subject
                  </label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    className="bg-white"
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    required
                  />
                </div> */}
              </div>
              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium">
                  Message
                </label>
                <Textarea
                  id="message"
                  rows={5}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="bg-white"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full sm:w-auto"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </Button>
            </form>

            <div className="mt-6 text-sm text-gray-500 space-y-4">
              <p>
                If you submit your request outside of normal business hours (9
                AM – 5 PM EST, Mon - Fri), the wait for an initial response may
                be longer than usual. If it's a weekend, we will get back to you
                on Monday.
              </p>

              <p>
                Please visit our{" "}
                <a
                  href="https://optionsapp.com/terms-conditions/"
                  target="_blank"
                  className="text-blue-600 hover:underline"
                >
                  Terms & Conditions
                </a>{" "}
                for any further information.
              </p>

              <p>
                Thank you for your patience and support.
                <br />
                The Options AI Team
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
